import { Button } from '@ctw/shared/components/button';
import { tw, twx } from '@ctw/shared/utils/tailwind';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { ReactNode } from 'react';

export interface PatientSummarizationBannerProps {
  tone: 'caution' | 'error';
  icon: IconDefinition;
  title?: string;
  body: ReactNode;
  action?: ReactNode;
  dismissable?: boolean;
  isDismissed?: boolean;
  onDismiss?: () => void;
  variant?: 'inline' | 'standalone';
  background?: 'tone' | 'white';
  testId?: string;
}

export const PatientSummarizationBanner = ({
  tone,
  icon,
  title,
  body,
  action,
  dismissable = false,
  isDismissed = false,
  onDismiss,
  variant = 'inline',
  background = 'tone',
  testId,
}: PatientSummarizationBannerProps) => {
  const isCaution = tone === 'caution';
  const isWhiteBg = background === 'white';

  let toneBg: string;
  if (isWhiteBg) {
    toneBg = 'bg-white';
  } else if (isCaution) {
    toneBg = 'bg-caution-background';
  } else {
    toneBg = 'bg-error-background';
  }

  const toneText = isCaution ? 'text-caution-text' : 'text-error-text';

  let toneBorder: string;
  if (isWhiteBg) {
    toneBorder = isCaution ? 'border-caution-badge' : 'border-error-badge';
  } else {
    toneBorder = isCaution ? 'border-caution-main/15' : 'border-error-main/15';
  }

  const content = (
    <div
      data-testid={testId}
      className={twx('flex items-start justify-between gap-4 p-4', toneBg, toneText, {
        [`${toneBorder} border-t-2`]: variant === 'inline',
        [`my-5 rounded-lg border ${toneBorder}`]:
          variant === 'standalone' && background !== 'white',
        [`my-5 rounded-lg border-2 ${toneBorder}`]:
          variant === 'standalone' && background === 'white',
      })}
    >
      {variant === 'standalone' ? (
        <div>
          <div className={tw`flex items-center gap-3`}>
            <FontAwesomeIcon icon={icon} className={tw`relative top-3 h-5 w-5`} />
            {title && <div className={tw`font-bold`}>{title}</div>}
          </div>
          <div className={twx('pl-8', { 'mt-0.5': Boolean(title) })}>{body}</div>
        </div>
      ) : (
        <div className={tw`flex items-start gap-2`}>
          <FontAwesomeIcon icon={icon} className={tw`h-4 w-4 pt-1`} />
          <div>
            {title && (
              <>
                <b>{title}</b>{' '}
              </>
            )}
            {body}
          </div>
        </div>
      )}
      <div className={tw`flex items-center gap-2`}>
        {action}
        {dismissable && (
          <Button
            type="button"
            variant="circular"
            size="xs"
            ariaLabel="Dismiss"
            className={twx(
              'flex items-center justify-center bg-transparent p-1 shadow-none transition-all duration-300 hover:bg-content-subtle/15 active:scale-[.98] active:bg-content-subtle/15',
              toneText,
            )}
            onClick={onDismiss}
          >
            <FontAwesomeIcon icon={faXmark} className={tw`h-4 w-4`} />
          </Button>
        )}
      </div>
    </div>
  );

  if (!dismissable) {
    return content;
  }

  return (
    <div
      className={twx('grid transition-[grid-template-rows,opacity] duration-300 ease-out', {
        'grid-rows-[0fr] opacity-0': isDismissed,
        'grid-rows-[1fr] opacity-100': !isDismissed,
      })}
    >
      <div className={tw`min-h-0 overflow-hidden`}>{content}</div>
    </div>
  );
};
